import { useState } from "react";
import toast from "../../utils/toast";
import {
  getAccountDeletionPreview,
  permanentlyDeleteAccounts,
} from "../../services/adminService";
import PermanentAccountDeletionModal from "./PermanentAccountDeletionModal";

const useAdminAccountDeletion = () => {
  const [accounts, setAccounts] = useState([]);
  const [originAccountId, setOriginAccountId] = useState(null);
  const [onSuccess, setOnSuccess] = useState(null);
  const [onCancel, setOnCancel] = useState(null);
  const [loading, setLoading] = useState(false);

  const openDeletion = async (
    accountType,
    resourceId,
    onSuccessCallback,
    onCancelCallback,
  ) => {
    setLoading(true);
    try {
      const result = await getAccountDeletionPreview({
        accountType,
        resourceId,
      });
      const linkedAccounts = result?.data?.accounts;
      if (!result?.success || !Array.isArray(linkedAccounts) || !linkedAccounts.length) {
        throw new Error(result?.message || "Could not load linked accounts.");
      }
      setAccounts(linkedAccounts);
      setOriginAccountId(result?.data?.originAccountId || linkedAccounts[0].id);
      setOnSuccess(() => onSuccessCallback);
      setOnCancel(() => onCancelCallback);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Could not prepare account deletion.",
      );
    } finally {
      setLoading(false);
    }
  };

  const clearDeletion = () => {
    setAccounts([]);
    setOriginAccountId(null);
    setOnSuccess(null);
    setOnCancel(null);
  };

  const cancelDeletion = () => {
    if (loading) return;
    const cancelCallback = onCancel;
    clearDeletion();
    cancelCallback?.();
  };

  const closeDeletion = () => {
    if (loading) return;
    clearDeletion();
  };

  const forceCloseDeletion = () => {
    clearDeletion();
  };

  const confirmDeletion = async (accountIds, confirmPendingFundraiserFunds) => {
    setLoading(true);
    try {
      const result = await permanentlyDeleteAccounts({
        accountIds,
        confirmation: "DELETE",
        confirmPendingFundraiserFunds,
      });
      if (!result?.success) {
        throw new Error(result?.message || "Account deletion failed.");
      }
      const accountCount = accountIds.length;
      forceCloseDeletion();
      toast.success(
        `${accountCount} account${accountCount === 1 ? "" : "s"} permanently deleted.`,
      );
      onSuccess?.();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Account deletion failed.",
      );
    } finally {
      setLoading(false);
    }
  };

  const modal = accounts.length ? (
    <PermanentAccountDeletionModal
      accounts={accounts}
      originAccountId={originAccountId}
      loading={loading}
      onCancel={cancelDeletion}
      onDelete={confirmDeletion}
    />
  ) : null;

  return { openDeletion, modal, loading };
};

export default useAdminAccountDeletion;
